import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../config/api';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { TICKET_COMPETITION_ORDER } from '../../config/ticketCompetitionTeamsUi';
import './AdminPages.css';

function parseLines(text) {
  const lines = String(text ?? '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const line of lines) {
    let s =
      line.length > 160
        ? line.slice(0, 160).trimEnd()
        : line;
    if (!seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
}

const AdminTicketOpponents = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usesCustomTeams, setUsesCustomTeams] = useState(false);
  const [error, setError] = useState(null);
  const [textByCompetition, setTextByCompetition] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get('/settings/ticket-competition-teams');
        if (cancelled) return;
        const nextText = {};
        for (const k of TICKET_COMPETITION_ORDER) {
          nextText[k] = (data.teamsByCompetition[k] || []).join('\n');
        }
        setTextByCompetition(nextText);
        setUsesCustomTeams(!!data.usesCustomTeams);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || 'Impossible de charger les listes');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setError(null);
    const snapshot = {};
    for (const k of TICKET_COMPETITION_ORDER) {
      snapshot[k] = parseLines(textByCompetition[k] || '');
    }
    try {
      setSaving(true);
      const { data } = await api.put('/settings/ticket-competition-teams', {
        teamsByCompetition: snapshot,
      });
      setUsesCustomTeams(true);
      if (data?.teamsByCompetition) {
        const nextText = {};
        for (const k of TICKET_COMPETITION_ORDER) {
          nextText[k] = (data.teamsByCompetition[k] || []).join('\n');
        }
        setTextByCompetition(nextText);
      }
      alert('Listes enregistrées.');
    } catch (e) {
      const msg =
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.message ||
        'Erreur';
      setError(msg);
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        'Réinitialiser toutes les listes sur les équipes livrées par défaut avec le site ?'
      )
    ) {
      return;
    }
    setError(null);
    try {
      setSaving(true);
      const { data } = await api.delete('/settings/ticket-competition-teams');
      setUsesCustomTeams(false);
      if (data?.teamsByCompetition) {
        const nextText = {};
        for (const k of TICKET_COMPETITION_ORDER) {
          nextText[k] = (data.teamsByCompetition[k] || []).join('\n');
        }
        setTextByCompetition(nextText);
      }
      alert('Listes réinitialisées.');
    } catch (e) {
      const msg =
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.message ||
        'Erreur';
      setError(msg);
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header" style={{ flexWrap: 'wrap', gap: '1rem' }}>
          <Link to="/admin/tickets" className="quick-link">
            <FaArrowLeft style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Retour aux billets
          </Link>
          <h1 style={{ margin: 0, flex: '1 1 auto' }}>
            Équipes adverses (par compétition)
          </h1>
          <button
            type="button"
            className="btn-add"
            onClick={handleSave}
            disabled={saving || loading}
          >
            <FaSave /> {saving ? 'Enregistrement…' : 'Enregistrer tout'}
          </button>
          <button
            type="button"
            className="btn-delete"
            onClick={handleReset}
            disabled={saving || loading}
          >
            Réinitialiser (défaut site)
          </button>
        </div>

        <p style={{ color: '#555', maxWidth: '900px', fontSize: '0.95rem' }}>
          Modifiez les noms utilisés comme <strong>équipe adverse</strong> lors de la création
          d’un billet (menus déroulants). Une ligne par équipe{' '}
          (variantes tolérées : synonymes comme « Milan » / « AC Milan », etc.). Ces listes sont
          enregistrées en base ; aucun fichier du site à éditer.
        </p>
        {!usesCustomTeams && !loading && (
          <p style={{ marginTop: '-0.5rem', fontSize: '0.9rem', color: '#7f8c8d' }}>
            Actuellement valeurs livrées par défaut avec le projet (vous pouvez enregistrer pour
            figer vos listes dans la base).
          </p>
        )}
        {error && (
          <p style={{ color: '#c0392b', marginTop: '-0.5rem' }}>
            <strong>{error}</strong>
          </p>
        )}

        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '1rem' }}>
            {TICKET_COMPETITION_ORDER.map((comp) => (
              <section key={comp} className="detail-section">
                <h3 style={{ marginTop: 0 }}>{comp}</h3>
                <textarea
                  value={textByCompetition[comp] || ''}
                  onChange={(e) =>
                    setTextByCompetition((prev) => ({
                      ...prev,
                      [comp]: e.target.value,
                    }))
                  }
                  placeholder="Une équipe par ligne"
                  spellCheck={false}
                  disabled={saving}
                  style={{
                    width: '100%',
                    minHeight: '160px',
                    fontFamily: 'inherit',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    resize: 'vertical',
                  }}
                />
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTicketOpponents;
